import {AnyAction, applyMiddleware, compose, createStore, Store} from "redux";
import reduxThunk from "redux-thunk";
import rootReducer from "./rootReducer";
import {PlayerReduxDispatch, PlayerReduxState, PlayerStage} from "./types";
import {Challenge, Scalars} from "../../schema";
import rootMiddleware from "./rootMiddleware";
import {Scenario} from "../../types";
import {UTM} from "../../libs/analytics";
import {ResponseTimeoutState} from "../../types/ResponseTimeoutState";

const composeEnhancers =
  (typeof window === "object" && (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

const enhancer = composeEnhancers(applyMiddleware(reduxThunk, rootMiddleware));

export const initialReduxState: Omit<
  PlayerReduxState,
  "client" | "audioManager" | "challengeId" | "challenge" | "scenarios"
> = {
  sessionId: null,
  sessionStartFailed: undefined,
  sessionResumeFailed: undefined,

  isPreview: false,
  isShared: false,
  isExternal: false,
  externalUserId: null,
  recognitionEngine: undefined,
  recognitionPipeline: undefined,

  level: 1,
  index: -1,
  maxIndexes: [-1],

  scenarioIndex: 0,

  mainScenarioLastIndex: -1,

  scenarioStack: [],
  finishedScenarios: [],

  branchId: undefined,
  branchIndex: undefined,

  exposedActions: [],
  chosenBranches: new Set(),
  chosenBranchesData: new Map(),
  isRecognizing: false,
  isHintConfirmation: false,

  interrupted: false,
  interruptReason: null,

  lastUserInputResult: null,
  dialogueHistory: [],
  currentActionAttempts: 0,
  feedbackSent: false,

  currentBackground: null,
  onPause: false,
  onVideoPlay: false,
  utm: null,
  adaptiveInput: null,
  responseTimeoutState: ResponseTimeoutState.NOT_STARTED,
  actionTimerAutoSubmit: false,
  actionTimerRemaining: null,
  rpgTurnKey: 0,
  stage: PlayerStage.Playing,
  isFeedbackPlayed: false,
  isFeedbackSkipped: false,
  initAgentAction: null,
};

type opts = {
  client: PlayerReduxState["client"];
  audioManager: PlayerReduxState["audioManager"];
  toggleRecorder: PlayerReduxState["toggleRecorder"];
  raiseProblem: (key: string) => boolean;
  askForCameraPermission?: boolean;
  challengeId: Scalars["ID"];
  challenge: Challenge;
  scenarios: Scenario[];
  isShared?: boolean;
  isPreview?: boolean;
  isExternal?: boolean;
  externalUserId?: Scalars["ID"];
  initialState?: Partial<typeof initialReduxState>;
  utm?: UTM;
  store?: Store<PlayerReduxState, AnyAction> & {dispatch: PlayerReduxDispatch};
};

export type PlayerReduxStore = ReturnType<typeof createPlayerReduxStore>;

export default function createPlayerReduxStore({
  client,
  audioManager,
  toggleRecorder,
  raiseProblem,
  challengeId,
  challenge,
  scenarios,
  isShared,
  isPreview,
  isExternal,
  externalUserId,
  initialState,
  utm,
}: opts) {
  return createStore(
    rootReducer,
    {
      client,
      audioManager,
      toggleRecorder,
      raiseProblem,
      challengeId,
      challenge,
      scenarios,
      ...initialReduxState,
      ...initialState,
      isShared: !!isShared,
      isPreview: !!isPreview,
      isExternal: !!isExternal,
      externalUserId: externalUserId ?? null,
      utm: utm ?? null,
      adaptiveInput: null,
    },
    enhancer,
  );
}
